// src/components/widgets/WidgetBuilder.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Code,
  Plus,
  Trash2,
  Copy,
  Search,
  Circle,
  Check,
  X,
  Pencil
} from 'lucide-react';

// shadcn components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';

// Widget components
import HeadlessWidgetEditor from './HeadlessWidgetEditor';
import EmbeddedWidgetEditor from './EmbeddedWidgetEditor';
import EmbedCodeModal from './EmbedCodeModal';
import CreateWidgetModal from './CreateWidgetModal';
import { LocalSidebarRail } from '../ui/local-sidebar-rail';

import PageContainer from '../ui/layout/PageContainer/PageContainer';
import { useWidgets } from '../../hooks/useWidgets';
import { cn } from '../../utils/cn';

const WidgetBuilder = ({ className = '' }) => {
  const {
    widgets,
    widgetsLoading,
    selectedWidget,
    loadWidgetDetails,
    updateWidget,
    deleteWidget,
    creatingWidget,
    savingWidget,
    publishingWidget,
    toggleWidgetStatus,
  } = useWidgets();

  // Local UI state
  const [embedWidgetId, setEmbedWidgetId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Canonical: Saving flag watchers
  const prevCreatingWidget = useRef(false);
  const prevSavingWidget = useRef(false);

  // Create modal — close on server confirmation
  useEffect(() => {
    if (prevCreatingWidget.current && !creatingWidget) {
      if (showCreateModal) {
        setShowCreateModal(false);
      }
    }
    prevCreatingWidget.current = creatingWidget;
  }, [creatingWidget, showCreateModal]);

  // Inline rename — clear editing state on server confirmation
  useEffect(() => {
    if (prevSavingWidget.current && !savingWidget) {
      if (editingWidgetId) {
        setEditingWidgetId(null);
        setEditingName('');
      }
    }
    prevSavingWidget.current = savingWidget;
  }, [savingWidget, editingWidgetId]);

  // Auto-select first widget on load if none selected
  useEffect(() => {
    if (!selectedWidget && widgets.length > 0 && !widgetsLoading) {
      console.log('WidgetBuilder: Auto-selecting first widget');
      loadWidgetDetails(widgets[0].id);
    }
  }, [selectedWidget, widgets, widgetsLoading]);

  // Handle new widget creation - opens modal
  const handleCreateWidget = () => {
    setShowCreateModal(true);
  };

  // Handle widget creation from modal
  const handleWidgetCreated = (createdWidget) => {
    // The new widget should automatically be selected by the store
  };

  // Handle embed code display (for headless widgets)
  const handleShowEmbed = (widgetId) => {
    setEmbedWidgetId(widgetId);
  };

  // Handle widget deletion
  const handleDeleteWidget = () => {
    if (!selectedWidget) return;

    const confirmed = window.confirm(
      `Delete "${selectedWidget.widget_name}"? This cannot be undone.`
    );

    if (confirmed) {
      deleteWidget(selectedWidget.id);
    }
  };

  // Handle active toggle
  const handleToggleActive = (checked) => {
    if (!selectedWidget) return;
    toggleWidgetStatus(selectedWidget.id, checked);
  };

  // Inline name editing handlers
  const handleStartEdit = (widget, e) => {
    e.stopPropagation();
    setEditingWidgetId(widget.id);
    setEditingName(widget.widget_name);
  };

  const handleCancelEdit = (e) => {
    e?.stopPropagation();
    setEditingWidgetId(null);
    setEditingName('');
  };

  const handleSaveEdit = (widget, e) => {
    e?.stopPropagation();
    if (!editingName.trim()) {
      handleCancelEdit();
      return;
    }
    updateWidget(widget.id, { widget_name: editingName.trim() });
    // Editing state clear handled by savingWidget flag watcher
  };

  // Filter widgets based on search
  const filteredWidgets = widgets.filter(widget =>
    !searchTerm ||
    widget.widget_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none" className={className}>
      <div className="flex h-full relative">
        {/* LEFT SIDEBAR - Widget List */}
        <div
          className={cn(
            "relative flex-shrink-0 transition-all duration-300 ease-in-out",
            leftPanelCollapsed ? "w-0" : "w-[320px]"
          )}
        >
          {/* Inner Container: Always 320px, slides in/out */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 w-[320px] border-r border-border bg-slate-900/50 flex flex-col transition-transform duration-300 ease-in-out",
              leftPanelCollapsed && "-translate-x-full"
            )}
          >
            {/* Hover Rail - Edge collapse/expand control */}
            <LocalSidebarRail
              collapsed={leftPanelCollapsed}
              onToggle={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              side="left"
            />

            {/* Header - Administrative Layer */}
            <div className="p-3 border-b flex-shrink-0" data-layer="administrative">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold">Widgets</h2>
                <Button
                  size="sm"
                  onClick={handleCreateWidget}
                  disabled={creatingWidget}
                  className="layer-button font-medium"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  New
                </Button>
              </div>

              {/* Search */}
              <div className="mb-3">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-slate-400 transition-colors duration-300 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search widgets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="layer-input pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Widget List */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {filteredWidgets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {searchTerm ? 'No widgets match your search' : 'No widgets found'}
                  </div>
                ) : (
                  filteredWidgets.map((widget) => (
                    <div
                      key={widget.id}
                      onClick={() => editingWidgetId !== widget.id && loadWidgetDetails(widget.id)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 rounded-md transition-all",
                        "flex items-start justify-between gap-2",
                        "border bg-card cursor-pointer",
                        "hover:bg-accent hover:shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:border-border hover:scale-[1.02]",
                        selectedWidget?.id === widget.id && "bg-accent shadow-[0_2px_8px_rgba(0,0,0,0.4)] border-primary scale-[1.02]",
                        selectedWidget?.id !== widget.id && "border-transparent shadow-[0_1px_2px_rgba(0,0,0,0.2)]",
                        "group"
                      )}
                    >
                      {editingWidgetId === widget.id ? (
                        <>
                          {/* Editing mode */}
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(widget, e);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit(e);
                              }
                            }}
                            className="h-7 text-sm flex-1"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={(e) => handleSaveEdit(widget, e)}
                          >
                            <Check className="h-3.5 w-3.5 text-emerald-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Normal mode */}
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-medium text-sm truncate">
                              {widget.widget_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant={widget.is_active ? 'default' : 'secondary'} className="text-xs">
                                {widget.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {widget.is_headless && (
                                <Badge variant="secondary" className="text-xs">
                                  Headless
                                </Badge>
                              )}
                              {widget.has_unpublished_changes && (
                                <Badge variant="outline" className="text-xs">
                                  Draft
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => handleStartEdit(widget, e)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Circle
                            className={cn(
                              "w-2 h-2 flex-shrink-0 mt-1.5",
                              widget.is_active
                                ? "fill-emerald-500 text-emerald-500"
                                : "fill-muted-foreground/50 text-muted-foreground/50"
                            )}
                          />
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Widget Actions */}
            {selectedWidget && (
              <>
                <Separator />
                <div className="p-3 space-y-3 flex-shrink-0">
                  {/* Active/Inactive Switch */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-card border">
                    <Label
                      htmlFor="widget-active-switch"
                      className={cn(
                        "text-sm font-medium cursor-pointer",
                        selectedWidget.is_active ? "text-emerald-400" : "text-muted-foreground"
                      )}
                    >
                      {selectedWidget.is_active ? 'Active' : 'Inactive'}
                    </Label>
                    <Switch
                      id="widget-active-switch"
                      checked={selectedWidget.is_active}
                      onCheckedChange={handleToggleActive}
                      disabled={savingWidget || publishingWidget}
                      className={cn(
                        selectedWidget.is_active && "data-[state=checked]:bg-emerald-500"
                      )}
                    />
                  </div>

                  {/* Embed Code Button - Only for headless widgets */}
                  {selectedWidget?.is_headless && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleShowEmbed(selectedWidget.id)}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Embed Code
                    </Button>
                  )}

                  {/* Delete Button */}
                  {!isConfirmingDelete ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setIsConfirmingDelete(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={handleDeleteWidget}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Confirm
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setIsConfirmingDelete(false)}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selectedWidget ? (
            /* Empty State */
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <Code className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
                <h2 className="text-2xl font-semibold mb-3">
                  Create your first widget
                </h2>
                <p className="text-muted-foreground mb-6">
                  Start by creating a booking widget to accept appointments on your website.
                </p>
                <Button
                  onClick={handleCreateWidget}
                  disabled={creatingWidget}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Create Widget
                </Button>
              </div>
            </div>
          ) : selectedWidget?.is_headless ? (
            // HEADLESS MODE - Show Card-based Configuration Editor
            <HeadlessWidgetEditor widgetId={selectedWidget.id} />
          ) : (
            // EMBEDDED MODE - Show Form-based Editor with Preview Tab
            <EmbeddedWidgetEditor widgetId={selectedWidget.id} />
          )}
        </div>
      </div>

      {/* Create Widget Modal */}
      <CreateWidgetModal
        isOpen={showCreateModal}
        onClose={() => { if (!creatingWidget) setShowCreateModal(false); }}
        onWidgetCreated={handleWidgetCreated}
      />

      {/* Embed Code Modal - For headless widgets */}
      <EmbedCodeModal
        widgetId={embedWidgetId}
        onClose={() => setEmbedWidgetId(null)}
      />
    </PageContainer>
  );
};

export default WidgetBuilder;
